/**
 * Pure helpers for parsing Firebase Functions callable errors on the
 * client. No Firebase types imported — we duck-type the error shape so
 * this helper works in any app + test environment.
 *
 * Why a shared parser: the customer-pwa, marketing site, dashboard, and
 * agent-pwa all call callables and all hit the same throttle/abuse
 * surfaces. Each should render a real "try again in 12s" screen, not a
 * generic "Internal error". This helper guarantees they parse the
 * structured `details` payload the same way — no per-app drift.
 *
 * See `apps/functions/src/lib/rate-limit.ts` for the server side that
 * emits these shapes. The contract:
 *
 *   - code  : `functions/resource-exhausted` (Firebase SDK prefix)
 *             OR `resource-exhausted` (raw HttpsError code without prefix)
 *   - details: { retryAfterSec: number, nextAction: string, endpoint: string }
 *
 * The helper returns `null` for any other error shape so the caller can
 * fall through to its existing branches (phone_required, validation, etc.).
 */

export type CallableNextAction = 'wait_then_retry' | 'contact_support' | 'switch_method';

export interface ParsedRateLimitError {
  /** Seconds until the window closes. >= 1 always. */
  readonly retryAfterSec: number;
  /** Hint for the UI on what the user's path forward looks like. */
  readonly nextAction: CallableNextAction;
  /** Which throttle fired — for telemetry + per-endpoint copy if needed. */
  readonly endpoint: string;
  /** The server-provided user-facing message (may be operator-flavoured). */
  readonly message: string;
}

/**
 * Try to interpret `err` as a rate-limit `resource-exhausted` error.
 * Returns the structured payload, or `null` if this isn't a rate-limit
 * error (so the caller can keep its existing error branches).
 */
export function parseRateLimitError(err: unknown): ParsedRateLimitError | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as { code?: unknown; details?: unknown; message?: unknown };
  const code = typeof e.code === 'string' ? e.code : '';
  if (code !== 'resource-exhausted' && code !== 'functions/resource-exhausted') {
    return null;
  }
  const details =
    e.details && typeof e.details === 'object' ? (e.details as Record<string, unknown>) : {};
  const retryAfterSec =
    typeof details['retryAfterSec'] === 'number'
      ? Math.max(1, Math.floor(details['retryAfterSec'] as number))
      : 60;
  const nextActionRaw =
    typeof details['nextAction'] === 'string'
      ? (details['nextAction'] as string)
      : 'wait_then_retry';
  const nextAction: CallableNextAction =
    nextActionRaw === 'contact_support' || nextActionRaw === 'switch_method'
      ? (nextActionRaw as CallableNextAction)
      : 'wait_then_retry';
  const endpoint = typeof details['endpoint'] === 'string' ? (details['endpoint'] as string) : '';
  const message =
    typeof e.message === 'string' && e.message.length > 0
      ? e.message
      : 'Easy on that button — try again in a moment.';
  return { retryAfterSec, nextAction, endpoint, message };
}
